import React from 'react';
import { theme } from '../../theme';

// <purpose>Rolling 12-month pill selector for StatistikSeite date filter.</purpose>
// <dependencies>theme</dependencies>
// <constraints>12 pills oldest→current left-to-right. Multi-select non-contiguous. Year suffix at boundaries only.</constraints>

const MONTH_LABELS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

export type MonthKey = string; // 'YYYY-MM'

export type MonthPill = { key: MonthKey; year: number; monthIdx: number; labelShort: string };

type Props = {
    selectedMonths: MonthKey[];
    onChange: (months: MonthKey[]) => void;
    ticketCountsByMonth: Record<MonthKey, number>;
    onOverflowClick?: () => void;
    referenceDate?: Date; // test override; defaults to new Date()
};

export function buildRollingMonths(now: Date): MonthPill[] {
    const months: MonthPill[] = [];
    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const yyyy = d.getFullYear();
        const mm = (d.getMonth() + 1).toString().padStart(2, '0');
        months.push({
            key: `${yyyy}-${mm}`,
            year: yyyy,
            monthIdx: d.getMonth(),
            labelShort: MONTH_LABELS_SHORT[d.getMonth()],
        });
    }
    return months;
}

export function pillDisplayLabel(m: MonthPill, prev: MonthPill | null): string {
    // Year suffix at boundaries: first pill OR year changes vs predecessor
    const showYear = prev === null || prev.year !== m.year;
    const yearTag = showYear ? ` '${(m.year % 100).toString().padStart(2, '0')}` : '';
    return `${m.labelShort}${yearTag}`;
}

export const DateFilterPills: React.FC<Props> = ({
    selectedMonths, onChange, ticketCountsByMonth, onOverflowClick, referenceDate,
}) => {
    const now = referenceDate ?? new Date();
    const months = buildRollingMonths(now);
    const currentKey = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;

    const toggle = (key: MonthKey) => {
        onChange(
            selectedMonths.includes(key)
                ? selectedMonths.filter(k => k !== key)
                : [...selectedMonths, key].sort()
        );
    };

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            <button
                data-pill-month="overflow"
                onClick={onOverflowClick}
                style={pillStyle({ selected: false, empty: true, isCurrent: false })}
                title="Älteren Zeitraum wählen"
                aria-label="Älteren Zeitraum wählen"
            >…</button>
            {months.map((m, i) => {
                const prev = i > 0 ? months[i - 1] : null;
                const isCurrent = m.key === currentKey;
                const labelText = pillDisplayLabel(m, prev);
                const selected = selectedMonths.includes(m.key);
                const empty = (ticketCountsByMonth[m.key] ?? 0) === 0;
                return (
                    <button
                        key={m.key}
                        data-pill-month={m.key}
                        onClick={() => toggle(m.key)}
                        style={pillStyle({ selected, empty, isCurrent })}
                        aria-pressed={selected}
                    >
                        {labelText}
                    </button>
                );
            })}
        </div>
    );
};

function pillStyle({ selected, empty, isCurrent }: { selected: boolean; empty: boolean; isCurrent: boolean }): React.CSSProperties {
    if (selected) {
        return {
            padding: '4px 10px',
            fontSize: 11,
            borderRadius: 12,
            fontWeight: 700,
            background: theme.colors.navy,
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            fontFamily: theme.font.family,
            transition: 'background-color 0.15s ease',
        };
    }
    if (empty) {
        // No tickets that month — transparent, italic, muted; still clickable
        return {
            padding: '4px 10px',
            fontSize: 11,
            borderRadius: 12,
            fontWeight: 400,
            fontStyle: 'italic',
            background: 'transparent',
            color: theme.colors.navyMuted,  // WCAG 1.4.3 — 4.55:1 on bgPage (was #c5c8d3, 1.54:1)
            border: 'none',
            cursor: 'pointer',
            fontFamily: theme.font.family,
            transition: 'background-color 0.15s ease',
        };
    }
    if (isCurrent) {
        // Current month, unselected — one tone deeper than has-tickets to signal "today's slot"
        return {
            padding: '4px 10px',
            fontSize: 11,
            borderRadius: 12,
            fontWeight: 700,
            background: '#e2e6f0',
            color: theme.colors.navy,
            border: 'none',
            cursor: 'pointer',
            fontFamily: theme.font.family,
            transition: 'background-color 0.15s ease',
        };
    }
    // Has tickets, not selected — solid pale-grey, navy text, no border
    return {
        padding: '4px 10px',
        fontSize: 11,
        borderRadius: 12,
        fontWeight: 600,
        background: '#f1f2f6',
        color: theme.colors.navy,
        border: 'none',
        cursor: 'pointer',
        fontFamily: theme.font.family,
        transition: 'background-color 0.15s ease',
    };
}
